// React glue over the corpus client. Uses React 19's `use()` to unwrap the
// cached fetch promises under Suspense — no data-fetching effect, no loading
// state plumbed by hand (react-useeffect-discipline; the parent renders a
// Suspense fallback). `use()` is the one hook permitted after an early return,
// so the not-found branch stays clean.

import { use } from "react";
import { useQuery } from "convex/react";
import { api } from "@bindery/convex/_generated/api";
import {
  fetchAnchors,
  fetchCatalog,
  fetchDocumentModel,
  findBook,
  findChapter,
  findMatter,
} from "@/lib/corpus";
import {
  flattenChapter,
  loadFlattener,
  loadMatterFlattener,
  type Flattener,
} from "@/lib/flatten-retelling";
import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type {
  CatalogBook,
  CatalogChapter,
  CatalogMatter,
  CorpusCatalog,
} from "@bindery/shared/corpus/catalog";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import { resolveAnchors } from "./fixtures/dev-anchors";

/** The full data needed to render one chapter's reading view. */
export interface ChapterData {
  book: CatalogBook;
  chapter: CatalogChapter;
  retellingModel: DocumentModel;
  originalModel: DocumentModel;
  anchors: CorpusAnchorPair[];
}

/** The validated catalog (suspends until loaded). */
export function useCatalog(): CorpusCatalog {
  return use(fetchCatalog());
}

/** The data needed to render one front/back matter page (PD-2). */
export interface MatterData {
  book: CatalogBook;
  matter: CatalogMatter;
  model: DocumentModel;
}

/**
 * Load a FREE matter document from the static corpus. Returns `null` when the
 * slug pair doesn't resolve (the caller renders not-found). No memoization is
 * needed here: matter has no sync hooks downstream, and every field is
 * referentially stable already (the promise cache).
 *
 * A paywalled document carries no `document` in the catalog — its text is never
 * emitted — so it resolves to `null` here and the reader routes it to
 * `usePremiumMatterData` behind the entitlement gate, exactly as it does for a
 * locked chapter.
 */
export function useMatterData(
  bookSlug: string,
  matterSlug: string,
): MatterData | null {
  const catalog = use(fetchCatalog());
  const book = findBook(catalog, bookSlug);
  const matter = book ? findMatter(book, matterSlug) : undefined;
  if (!book || !matter || matter.premium || matter.document === undefined) {
    return null;
  }
  return { book, matter, model: use(fetchDocumentModel(matter.document)) };
}

/**
 * Single-slot "memoize the last call" cache for the object `useChapterData`
 * returns. Not a React hook — deliberately: `use()` is uniquely exempt from
 * the rules of hooks (it may be called after the early return below), but an
 * ordinary `useMemo` there would not be. `book`/`chapter`/`retellingModel`/
 * `originalModel`/`corpusAnchors` are already referentially stable across
 * re-renders (the promise cache), but building the `ChapterData` object — and
 * `resolveAnchors`'s array spread — is not; without this, the sync hooks'
 * `anchors` dependency changes identity on every unrelated parent re-render,
 * tearing down and re-running `useSyncScroll`/`useHardAlign`.
 */
let cache: {
  book: CatalogBook;
  chapter: CatalogChapter;
  retellingModel: DocumentModel;
  originalModel: DocumentModel;
  corpusAnchors: readonly CorpusAnchorPair[];
  result: ChapterData;
} | null = null;

function memoizedChapterData(
  bookSlug: string,
  chapterNumber: number,
  book: CatalogBook,
  chapter: CatalogChapter,
  retellingModel: DocumentModel,
  originalModel: DocumentModel,
  corpusAnchors: readonly CorpusAnchorPair[],
): ChapterData {
  if (
    cache &&
    cache.book === book &&
    cache.chapter === chapter &&
    cache.retellingModel === retellingModel &&
    cache.originalModel === originalModel &&
    cache.corpusAnchors === corpusAnchors
  ) {
    return cache.result;
  }
  const result: ChapterData = {
    book,
    chapter,
    retellingModel,
    originalModel,
    anchors: resolveAnchors(bookSlug, chapterNumber, corpusAnchors),
  };
  cache = {
    book,
    chapter,
    retellingModel,
    originalModel,
    corpusAnchors,
    result,
  };
  return result;
}

/**
 * Load a chapter's retelling + original models and anchors. Returns `null` when
 * the slug/number don't resolve (the caller renders a not-found state). The
 * three artifact fetches are kicked off before the first `use()` suspends, so
 * they run in parallel rather than waterfalling.
 */
export function useChapterData(
  bookSlug: string,
  chapterNumber: number,
): ChapterData | null {
  const catalog = use(fetchCatalog());
  const book = findBook(catalog, bookSlug);
  const chapter = book ? findChapter(book, chapterNumber) : undefined;
  if (!book || !chapter) return null;
  // A premium (locked) chapter carries no retelling/anchors in the static
  // corpus — its text is served through `premium.getChapter`. The free loader
  // has nothing to fetch, so it returns null; the reader routes premium
  // chapters to `usePremiumChapterData` behind the entitlement gate (plan 10).
  if (
    chapter.premium ||
    chapter.retelling === undefined ||
    chapter.anchors === undefined
  ) {
    return null;
  }

  const retellingPromise = fetchDocumentModel(chapter.retelling);
  const originalPromise = fetchDocumentModel(chapter.original);
  const anchorsPromise = fetchAnchors(chapter.anchors);

  const retellingModel = use(retellingPromise);
  const originalModel = use(originalPromise);
  const corpusAnchors = use(anchorsPromise);

  return memoizedChapterData(
    bookSlug,
    chapterNumber,
    book,
    chapter,
    retellingModel,
    originalModel,
    corpusAnchors.anchors,
  );
}

// --------------------------------------------------------------- premium path

/** The gated premium payload from `premium.getChapter` (never `undefined` here). */
type PremiumPayload = NonNullable<ReturnType<typeof usePremiumQuery>>;

/**
 * Single-slot memo for the premium `ChapterData`, mirroring `memoizedChapterData`
 * (use-corpus.ts:75): Convex `useQuery` returns a stable reference until the
 * data changes, and the static original + flattener are already stable, so we
 * only rebuild the flattened retelling — and the `ChapterData` object the sync
 * hooks depend on — when an input identity actually changes.
 */
let premiumCache: {
  book: CatalogBook;
  chapter: CatalogChapter;
  originalModel: DocumentModel;
  premium: PremiumPayload;
  flatten: Flattener;
  result: ChapterData;
} | null = null;

function memoizedPremiumChapterData(
  bookSlug: string,
  book: CatalogBook,
  chapter: CatalogChapter,
  originalModel: DocumentModel,
  premium: PremiumPayload,
  flatten: Flattener,
): ChapterData {
  if (
    premiumCache &&
    premiumCache.book === book &&
    premiumCache.chapter === chapter &&
    premiumCache.originalModel === originalModel &&
    premiumCache.premium === premium &&
    premiumCache.flatten === flatten
  ) {
    return premiumCache.result;
  }
  const retellingModel = flattenChapter(flatten, {
    bookSlug,
    chapterNumber: chapter.number,
    title: premium.title,
    blocks: premium.blocks,
  });
  const result: ChapterData = {
    book,
    chapter,
    retellingModel,
    originalModel,
    anchors: premium.anchors,
  };
  premiumCache = {
    book,
    chapter,
    originalModel,
    premium,
    flatten,
    result,
  };
  return result;
}

/** Narrow wrapper so the memo's `premium` param carries the real query type. */
function usePremiumQuery(bookSlug: string, chapterNumber: number) {
  return useQuery(api.premium.getChapter, { bookSlug, chapterNumber });
}

/** Result of a premium chapter load: still resolving, missing, or ready. */
export type PremiumChapterData = ChapterData | null | "loading";

/**
 * Load an ENTITLED premium chapter's `ChapterData` (plan 10 §4). The retelling
 * text + anchors come from the gated `premium.getChapter` query (reactive — a
 * fresh publish flips instantly); the original is the ordinary static corpus
 * fetch; the two are flattened with the shared pure flattener. Returns
 * `"loading"` while the gated query resolves, `null` when the chapter isn't in
 * the catalog. Must be rendered only behind the entitlement gate — the query
 * throws `forbidden`/`unauthenticated` otherwise (the error boundary catches a
 * mid-read session lapse; the reactive gate then re-offers sign-in).
 */
export function usePremiumChapterData(
  bookSlug: string,
  chapterNumber: number,
): PremiumChapterData {
  const premium = usePremiumQuery(bookSlug, chapterNumber);
  const catalog = use(fetchCatalog());
  const book = findBook(catalog, bookSlug);
  const chapter = book ? findChapter(book, chapterNumber) : undefined;
  if (!book || !chapter || chapter.original === undefined) return null;

  const originalModel = use(fetchDocumentModel(chapter.original));
  const flatten = use(loadFlattener());

  if (premium === undefined) return "loading";
  return memoizedPremiumChapterData(
    bookSlug,
    book,
    chapter,
    originalModel,
    premium,
    flatten,
  );
}

/** Result of a locked matter load: still resolving, missing, or ready. */
export type PremiumMatterData = MatterData | null | "loading";

/**
 * Load an ENTITLED paywalled matter document. `usePremiumChapterData`'s
 * counterpart, and shorter for the same reasons the matter reader is: there is
 * no original to fetch alongside and no anchors to reconcile, so the gated
 * blocks flatten straight into the one model the page renders.
 *
 * No memo slot. The premium chapter path needs one because its `ChapterData`
 * feeds the sync hooks, whose dependency identity must not churn; matter has no
 * sync hooks, which is the same reason `useMatterData` above skips it too.
 *
 * Must be rendered only behind the entitlement gate — `premium.getMatter`
 * throws `forbidden`/`unauthenticated` otherwise.
 */
export function usePremiumMatterData(
  bookSlug: string,
  matterSlug: string,
): PremiumMatterData {
  const gated = useQuery(api.premium.getMatter, { bookSlug, matterSlug });
  const catalog = use(fetchCatalog());
  const book = findBook(catalog, bookSlug);
  const matter = book ? findMatter(book, matterSlug) : undefined;
  const flatten = use(loadMatterFlattener());
  if (!book || !matter) return null;
  if (gated === undefined) return "loading";
  return {
    book,
    matter,
    model: flatten({ title: gated.title, blocks: gated.blocks }),
  };
}
